/*
 * Fetches published CMS content once after mount and overlays it on the
 * compiled-in defaults. Prerendered HTML keeps working for SEO; visitors
 * see the latest published content instantly — no rebuild required.
 *
 * If the API is unreachable (e.g. no backend deployed yet), the site is
 * fully functional on the site.js defaults — graceful degradation.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { runtime, applyContent } from '../data/runtime';

const SiteCtx = createContext(runtime);

export const SiteProvider = ({ children }) => {
  const [data, setData] = useState(runtime);

  useEffect(() => {
    let alive = true;
    fetch('/api/public/content')
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => {
        if (alive && payload) setData({ ...applyContent(payload) });
      })
      .catch(() => { /* defaults already in place */ });
    return () => { alive = false; };
  }, []);

  const value = useMemo(() => ({
    ...data,
    getPackage: (id) => data.PACKAGES.find((p) => p.id === id),
    getServicePage: (slug) => data.SERVICE_PAGES.find((s) => s.slug === slug),
    getCity: (id) => data.CITIES.find((c) => c.id === id),
    getPage: (slug) => data.PAGES.find((p) => p.slug === slug),
    getSeo: (path) => data.SEO.find((s) => s.path === path)
  }), [data]);

  return <SiteCtx.Provider value={value}>{children}</SiteCtx.Provider>;
};

export const useSite = () => useContext(SiteCtx);
